import React, { useState, useEffect } from 'react';
import { FaTimes, FaDownload, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

function TableModal({ isOpen, onClose, data, title = "Data Table" }) {
    const [sortConfig, setSortConfig] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isMobile, setIsMobile] = useState(false);
    const itemsPerPage = 20;

    const headers = data && data.length > 0 ? Object.keys(data[0]) : [];

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const sortedData = React.useMemo(() => {
        if (!data || data.length === 0 || !sortConfig) return data || [];

        return [...data].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [data, sortConfig]);

    const totalPages = Math.ceil((sortedData?.length || 0) / itemsPerPage);
    const paginatedData = sortedData?.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    ) || [];

    if (!isOpen || !data || data.length === 0) return null;

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev?.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const exportCSV = () => {
        const csvContent = [
            headers.join(','),
            ...sortedData.map(row =>
                headers.map(header =>
                    typeof row[header] === 'string' && row[header].includes(',')
                        ? `"${row[header]}"`
                        : row[header]
                ).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title.toLowerCase().replace(/\s+/g, '_')}_export.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '10px' : '20px'
        },
        modal: {
            backgroundColor: 'white',
            borderRadius: isMobile ? '8px' : '12px',
            width: isMobile ? '100%' : '95vw',
            height: isMobile ? '100%' : '90vh',
            maxWidth: isMobile ? 'none' : '1200px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
        },
        header: {
            padding: isMobile ? '15px' : '20px',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '10px'
        },
        title: {
            margin: 0,
            fontSize: isMobile ? '1.1rem' : '1.25rem',
            fontWeight: '600',
            order: isMobile ? 1 : 0,
            width: isMobile ? '100%' : 'auto'
        },
        controls: {
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            order: isMobile ? 2 : 0,
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-end'
        },
        button: {
            padding: isMobile ? '10px 14px' : '8px 12px',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: isMobile ? '16px' : '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: isMobile ? '44px' : 'auto'
        },
        exportButton: {
            backgroundColor: '#28a745',
            color: 'white'
        },
        closeButton: {
            backgroundColor: '#6c757d',
            color: 'white'
        },
        tableContainer: {
            flex: 1,
            overflow: 'auto',
            padding: isMobile ? '10px' : '0 20px',
            WebkitOverflowScrolling: 'touch'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: isMobile ? '12px' : '14px',
            minWidth: isMobile ? '600px' : 'auto'
        },
        th: {
            padding: isMobile ? '8px 6px' : '12px 8px',
            backgroundColor: '#0c022f',
            color: 'white',
            borderBottom: '2px solid #dee2e6',
            textAlign: 'left',
            fontWeight: '600',
            position: 'sticky',
            top: 0,
            cursor: 'pointer',
            userSelect: 'none',
            zIndex: 1,
            fontSize: isMobile ? '11px' : '14px',
            minWidth: isMobile ? '80px' : 'auto'
        },
        td: {
            padding: isMobile ? '8px 6px' : '10px 8px',
            borderBottom: '1px solid #dee2e6',
            wordBreak: 'break-word',
            maxWidth: isMobile ? '120px' : 'none'
        },
        pagination: {
            padding: isMobile ? '15px 10px' : '20px',
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '10px'
        },
        pageInfo: {
            fontSize: isMobile ? '12px' : '14px',
            color: '#6c757d',
            order: isMobile ? 2 : 0,
            width: isMobile ? '100%' : 'auto',
            textAlign: isMobile ? 'center' : 'left'
        },
        pageButtons: {
            display: 'flex',
            gap: isMobile ? '3px' : '5px',
            order: isMobile ? 1 : 0
        },
        pageButton: {
            padding: isMobile ? '8px 12px' : '6px 12px',
            border: '1px solid #dee2e6',
            backgroundColor: 'white',
            cursor: 'pointer',
            borderRadius: '4px',
            fontSize: isMobile ? '14px' : '14px',
            minHeight: isMobile ? '40px' : 'auto',
            minWidth: isMobile ? '40px' : 'auto'
        },
        activePageButton: {
            backgroundColor: '#0c022f',
            color: 'white',
            borderColor: '#0c022f'
        },
        scrollHint: {
            display: isMobile ? 'block' : 'none',
            textAlign: 'center',
            padding: '8px',
            fontSize: '12px',
            color: '#6c757d',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef'
        }
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h3 style={styles.title}>{title}</h3>
                    <div style={styles.controls}>
                        <button
                            style={{ ...styles.button, ...styles.exportButton }}
                            onClick={exportCSV}
                        >
                            <FaDownload /> {isMobile ? 'Export' : 'Export CSV'}
                        </button>
                        <button
                            style={{ ...styles.button, ...styles.closeButton }}
                            onClick={onClose}
                        >
                            <FaTimes /> Close
                        </button>
                    </div>
                </div>

                {isMobile && (
                    <div style={styles.scrollHint}>
                        Swipe left/right to scroll table horizontally
                    </div>
                )}

                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                {headers.map(header => (
                                    <th
                                        key={header}
                                        style={styles.th}
                                        onClick={() => handleSort(header)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            {isMobile ? header.substring(0, 8) + (header.length > 8 ? '...' : '') : header}
                                            {sortConfig?.key === header ? (
                                                sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                                            ) : (
                                                <FaSort style={{ opacity: 0.3 }} />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((row, index) => (
                                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                                    {headers.map(header => (
                                        <td key={header} style={styles.td} title={row[header]?.toString()}>
                                            {isMobile && row[header]?.toString().length > 15
                                                ? row[header]?.toString().substring(0, 15) + '...'
                                                : row[header]?.toString() || ''}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div style={styles.pagination}>
                        <div style={styles.pageButtons}>
                            <button
                                style={{ ...styles.pageButton, opacity: currentPage === 1 ? 0.5 : 1 }}
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                            >
                                {isMobile ? '‹' : 'Previous'}
                            </button>

                            {[...Array(Math.min(isMobile ? 3 : 5, totalPages))].map((_, i) => {
                                const pageNum = Math.max(1, Math.min(totalPages - (isMobile ? 2 : 4), currentPage - (isMobile ? 1 : 2))) + i;
                                return (
                                    <button
                                        key={pageNum}
                                        style={{
                                            ...styles.pageButton,
                                            ...(pageNum === currentPage ? styles.activePageButton : {})
                                        }}
                                        onClick={() => setCurrentPage(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}

                            <button
                                style={{ ...styles.pageButton, opacity: currentPage === totalPages ? 0.5 : 1 }}
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                            >
                                {isMobile ? '›' : 'Next'}
                            </button>
                        </div>

                        <div style={styles.pageInfo}>
                            {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TableModal;